import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link, useOutletContext } from 'react-router-dom';
import { SearchLayoutContextType } from '../layouts/SearchLayout';
import { useTranslation } from 'react-i18next';
import '../App.css';
import './SearchResults.css';
import api from '../services/api';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import ReactMarkdown from 'react-markdown';

interface SearchResultItem {
    // code_article (V2) renvoie un id ES composite (chaîne, ex. "24_501") ;
    // les autres types renvoient un id MySQL entier.
    bertilaw_id: string | number;
    text: string;
    score: number;
    juridiction?: string;
    chambre?: string;
    cour?: string;
    numero?: string;
    date?: string;
    title?: string;
    auteur?: string;
    editeur?: string;
    isbn?: string;
    ebook?: string;
    link?: string;
    from?: string;
    code_name?: string;
    law_title?: string;
    modif_law_title?: string;
}

interface SearchResultsData {
    classification: string | number | null;
    code_articles: Record<string, Record<string, SearchResultItem[]>>;
    constitution: SearchResultItem[];
    jurisprudences: SearchResultItem[];
    joarticles: SearchResultItem[];
    livres: SearchResultItem[];
    theses: SearchResultItem[];
    error?: string;
}

// Helper component for text truncation with HTML support (for Meilisearch highlights)
// Helper component for text truncation with HTML support
const TextWithShowMore: React.FC<{ text: string; maxLength?: number }> = ({ text, maxLength = 300 }) => {
    const [expanded, setExpanded] = useState(false);
    const { t } = useTranslation();

    // Strip HTML for length calculation (DOMParser : document inerte, aucun handler on* exécuté)
    const stripHtml = (html: string) => {
        return new DOMParser().parseFromString(html, "text/html").body.textContent || "";
    };

    const plainText = stripHtml(text || "");
    const shouldTruncate = plainText.length > maxLength;

    // Truncate by finding a good break point in the plain text
    const getTruncatedHtml = () => {
        if (!shouldTruncate) return text;
        // For safety, limit HTML render length but allow marks to complete
        const truncated = text.substring(0, maxLength + 100);
        // Find the last complete tag
        const lastMark = truncated.lastIndexOf("</mark>");
        if (lastMark > maxLength - 50) {
            return truncated.substring(0, lastMark + 7) + "...";
        }
        return truncated + "...";
    };

    if (!shouldTruncate) {
        return <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(text) }} />;
    }

    return (
        <div>
            {expanded ? (
                <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(text) }} />
            ) : (
                <div className="mb-1 text-muted small" dangerouslySetInnerHTML={{ __html: sanitizeHtml(getTruncatedHtml()) }} />
            )}
            <button
                className="btn btn-link btn-sm p-0 text-decoration-none btn-show-more"
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setExpanded(!expanded);
                }}
            >
                {expanded ? t('search.showLess') : t('search.showMore')}
            </button>
        </div>
    );
};

// Pagination Component
const Pagination: React.FC<{
    pagination: any;
    onPageChange: (page: number) => void;
}> = ({ pagination, onPageChange }) => {
    const { t, i18n } = useTranslation();

    if (!pagination || pagination.total_pages <= 1) return null;

    const { page, total_pages, total, limit } = pagination;
    const isRTL = i18n.language === 'ar';

    return (
        <div className="d-flex justify-content-center mb-3">
            <div className={`pagination-container animate-fade-in d-flex align-items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                <button
                    className="page-btn"
                    onClick={() => onPageChange(page - 1)}
                    disabled={page <= 1}
                    title={t('search.previous')}
                >
                    <i className="bi bi-chevron-left"></i>
                </button>

                <span className="page-info">
                    {page} / {total_pages}
                </span>

                <button
                    className="page-btn"
                    onClick={() => onPageChange(page + 1)}
                    disabled={page >= total_pages}
                    title={t('search.next')}
                >
                    <i className="bi bi-chevron-right"></i>
                </button>

                <div className={`text-muted small ${isRTL ? 'me-3 border-end pe-3' : 'ms-3 border-start ps-3'}`}>
                    {t('search.showing')} {Math.min((page - 1) * limit + 1, total)} - {Math.min(page * limit, total)} {t('search.of')} {total}
                </div>
            </div>
        </div>
    );
};

const SearchResults: React.FC<{ results?: SearchResultsData }> = ({ results: initialResults }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const { showMobileFilters, setShowMobileFilters } = useOutletContext<SearchLayoutContextType>() || { showMobileFilters: false, setShowMobileFilters: () => { } };

    // Read all parameters from URL
    const urlParams = new URLSearchParams(location.search);
    const query = urlParams.get('q');

    const [currentQuery, setCurrentQuery] = useState(query || '');
    const [results, setResults] = useState<SearchResultsData | null>(initialResults || null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [viewMode, setViewMode] = useState<'full' | 'card'>(urlParams.get('display') === 'grid' ? 'card' : 'full');
    const [sortBy, setSortBy] = useState<'relevance' | 'date'>(urlParams.get('sort_by') === 'date' ? 'date' : 'relevance');
    const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>(urlParams.get('sort_order') === 'asc' ? 'asc' : 'desc'); // Sort order for results
    const [limit, setLimit] = useState(10);
    const [page, setPage] = useState(parseInt(urlParams.get('page') || '1'));
    const [pagination, setPagination] = useState<any>(null);


    // Favorites state: {doc_type: [ids]}
    const [favorites, setFavorites] = useState<Record<string, (string | number)[]>>({});

    // Fetch user favorites on mount
    useEffect(() => {
        const fetchFavorites = async () => {
            try {
                const res = await api.get('/favorites/ids');
                setFavorites(res.data || {});
            } catch (err) {
                // User not logged in or error - favorites won't show
                console.log('Could not fetch favorites');
            }
        };
        fetchFavorites();
    }, []);

    // Check if document is in favorites (comparaison type-agnostique :
    // code_article = id ES composite (chaîne), autres types = id MySQL entier)
    const isFavorite = (docType: string, docId: string | number): boolean => {
        return favorites[docType]?.some(id => String(id) === String(docId)) || false;
    };

    // Toggle favorite status
    const toggleFavorite = async (docType: string, docId: string | number, title: string) => {
        const wasFavorite = isFavorite(docType, docId);
        try {
            if (wasFavorite) {
                await api.delete('/favorites', { data: { document_type: docType, document_id: docId } });
                setFavorites(prev => ({
                    ...prev,
                    [docType]: (prev[docType] || []).filter(id => String(id) !== String(docId))
                }));
            } else {
                await api.post('/favorites', { document_type: docType, document_id: docId, title });
                setFavorites(prev => ({
                    ...prev,
                    [docType]: [...(prev[docType] || []), docId]
                }));
            }
        } catch (err: any) {
            if (err.response?.status === 401) {
                alert(t('search.loginToFavorite', 'Connectez-vous pour ajouter aux favoris'));
            }
        }
    };

    // Map frontend domain to backend doc_type
    const domainToDocType: Record<string, string> = {
        'code_articles': 'code_article',
        'jurisprudences': 'jps',
        'joarticles': 'joarticles',
        'livres': 'livres',
        'theses': 'theses',
        'revue_articles': 'livres' // Revue uses same backend as livres
    };

    // Initialize filter states from URL parameters
    // Reverse map for handling legacy/direct doc_type params
    const docTypeToDomain: Record<string, string> = {
        'code_article': 'code_articles',
        'jps': 'jurisprudences',
        'joarticles': 'joarticles',
        'livres': 'livres',
        'theses': 'theses'
    };

    let domain = urlParams.get('domain');
    const paramDocType = urlParams.get('doc_type');

    // If domain is missing but doc_type is present and maps to a domain, use it
    if (!domain && paramDocType && docTypeToDomain[paramDocType]) {
        domain = docTypeToDomain[paramDocType];
    }

    domain = domain || 'code_articles';
    const docType = domainToDocType[domain] || 'code_article';
    const docTypeFilter = urlParams.get('doc_type') || ''; // Specific doc type filter (loi, decret, etc.)
    const dateStart = urlParams.get('date_start') || '';
    const dateEnd = urlParams.get('date_end') || '';
    const joSignatureDateStart = urlParams.get('jo_signature_date_start') || '';
    const joSignatureDateEnd = urlParams.get('jo_signature_date_end') || '';
    const joYear = urlParams.get('jo_year') || '';
    const joPublicationDateStart = urlParams.get('jo_publication_date_start') || '';
    const joPublicationDateEnd = urlParams.get('jo_publication_date_end') || '';
    const typeParam = urlParams.get('type') || 'corpus';
    const joJournalNum = urlParams.get('jo_journal_num') || '';
    const joTextNum = urlParams.get('jo_text_num') || '';
    const cour = urlParams.get('cour') || '';
    const codeId = urlParams.get('code_id') ? parseInt(urlParams.get('code_id')!) : '';
    const lawType = urlParams.get('law_type') || docTypeFilter; // Use doc_type as law_type for filtering
    const chambreId = urlParams.get('chambre_id') ? parseInt(urlParams.get('chambre_id')!) : '';

    const juridictionId = urlParams.get('juridiction_id') ? parseInt(urlParams.get('juridiction_id')!) : '';
    const natureIdParams = urlParams.get('nature_id');
    const natureIds = natureIdParams ? natureIdParams.split(',').map(Number).filter(n => !isNaN(n)) : [];

    // Search mode options
    const wholeWord = urlParams.get('whole_word') === 'true';
    const anyWord = urlParams.get('any_word') === 'true';
    const strictSearch = urlParams.get('strict_search') === 'true';
    const searchField = urlParams.get('search_field') || 'all';

    // Sync currentQuery with URL query when URL changes
    useEffect(() => {
        if (query) {
            setCurrentQuery(query);
        }
    }, [query]);

    // Sync page from URL params when URL changes
    useEffect(() => {
        const urlPage = parseInt(urlParams.get('page') || '1');
        if (urlPage !== page) {
            setPage(urlPage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    // Handle page change - update state and URL
    const handlePageChange = (newPage: number) => {
        setPage(newPage);
        // Update URL with new page
        const params = new URLSearchParams(location.search);
        params.set('page', String(newPage));
        navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    };

    // Handle view mode change - update state and URL
    const handleViewModeChange = (mode: 'full' | 'card') => {
        setViewMode(mode);
        // Update URL with new display mode
        const params = new URLSearchParams(location.search);
        params.set('display', mode === 'card' ? 'grid' : 'list');
        navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    };

    // Handle sort change - update state and URL
    const handleSortChange = (newSortBy: 'relevance' | 'date', newSortOrder: 'desc' | 'asc') => {
        setSortBy(newSortBy);
        setSortOrder(newSortOrder);
        // Update URL with new sort params
        const params = new URLSearchParams(location.search);
        params.set('sort_by', newSortBy);
        params.set('sort_order', newSortOrder);
        navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    };

    useEffect(() => {
        // Fetch results even if query is empty (to show "all" or default results)
        // Also re-fetch when language changes to get content in the new language
        fetchResults();
        // eslint-disable-next-line react-hooks/exhaustive-deps

    }, [query, limit, page, docType, cour, codeId, lawType, chambreId, juridictionId, natureIdParams, wholeWord, anyWord, strictSearch, searchField, dateStart, dateEnd, joSignatureDateStart, joSignatureDateEnd, joYear, joPublicationDateStart, joPublicationDateEnd, typeParam, joJournalNum, joTextNum, sortBy, sortOrder, i18n.language]);

    const fetchResults = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await api.post('/search/semantic', {
                question: query || '',
                lang: i18n.language,  // Pass user's UI language preference
                max_results: limit,
                page: page,
                doc_type: docType || undefined,
                date_start: dateStart || undefined,
                date_end: dateEnd || undefined,
                jo_signature_date_start: joSignatureDateStart || undefined,
                jo_signature_date_end: joSignatureDateEnd || undefined,
                jo_year: joYear || undefined,
                jo_publication_date_start: joPublicationDateStart || undefined,
                jo_publication_date_end: joPublicationDateEnd || undefined,
                type: typeParam || 'corpus',
                jo_journal_num: joJournalNum || undefined,
                jo_text_num: joTextNum || undefined,
                cour: cour || undefined,
                code_id: codeId || undefined,
                law_title: lawType || undefined,
                chambre_id: chambreId || undefined,

                juridiction_id: juridictionId || undefined,
                nature_id: natureIds.length > 0 ? natureIds : undefined,
                whole_word: wholeWord || undefined,
                any_word: anyWord || undefined,
                strict_search: strictSearch || undefined,
                search_field: searchField || undefined,
                sort_by: sortBy || undefined,
                sort_order: sortOrder || undefined
            });
            setResults(response.data);

            if (response.data.pagination) {
                setPagination(response.data.pagination);
            }
        } catch (err) {
            setError(t('search.error'));
            console.error(err);
        } finally {
            setLoading(false);
        }
    };


    const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (currentQuery.trim()) {
            navigate(`/${i18n.language}/search?q=${encodeURIComponent(currentQuery)}`);
        }
    };

    // Rendu d'un item de jurisprudence
    const JurisprudenceItem = ({ item, viewMode }: { item: any, viewMode: 'full' | 'card' }) => {
        const { t, i18n } = useTranslation();
        const isArabic = i18n.language === 'ar';
        const otherLang = isArabic ? 'fr' : 'ar';

        const containerClass = viewMode === 'card' ? 'col-md-6 col-lg-4 col-xl-3 mb-4' : 'col-12 mb-3';

        return (
            <div className={containerClass}>
                <div className="result-card type-jps animate-fade-in">
                    <div className="result-card-body">
                        {/* Header: Badge, Numero, Date */}
                        <div className="d-flex w-100 justify-content-between align-items-center mb-2">
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                <span className="premium-badge badge-jps">
                                    <i className="bi bi-bank me-1"></i>
                                    {t('search.types.jps')}
                                </span>
                                {item.numero && item.numero !== '0' && (
                                    <span className="fw-bold text-dark">
                                        <i className="bi bi-hash"></i><span dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.numero) }} />
                                    </span>
                                )}
                                {item.date && (
                                    <small className="text-muted fw-medium">
                                        <i className="bi bi-calendar3 me-1"></i>
                                        {item.date}
                                    </small>
                                )}
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <button
                                    className="btn p-0 fav-btn"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        toggleFavorite('jurisprudence', item.bertilaw_id, `${item.juridiction || ''} - ${item.date || ''}`);
                                    }}
                                    title={isFavorite('jurisprudence', item.bertilaw_id) ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                                >
                                    <i className={`bi ${isFavorite('jurisprudence', item.bertilaw_id) ? 'bi-star-fill' : 'bi-star'}`}></i>
                                </button>
                            </div>
                        </div>

                        {/* Language availability warning */}
                        {!item.has_content && item.has_other_lang && (
                            <div className="alert alert-warning py-2 px-3 mb-2 d-flex align-items-center justify-content-between" style={{ fontSize: '0.85rem' }}>
                                <span>
                                    <i className="bi bi-exclamation-triangle me-2"></i>
                                    {isArabic ? 'غير متوفر بالعربية' : 'Non disponible en français'}
                                </span>
                                <Link
                                    to={`/${otherLang}/detail/jurisprudence/${item.bertilaw_id}`}
                                    className="btn btn-sm btn-outline-warning"
                                >
                                    {isArabic ? 'Voir en français' : 'عرض بالعربية'}
                                </Link>
                            </div>
                        )}

                        {/* Principe (separate line under header) */}
                        {item.principe && (
                            <div className="principe-line mb-3">
                                <Link
                                    to={`/${i18n.language}/detail/jurisprudence/${item.bertilaw_id}`}
                                    state={{ searchParams: location.search }}
                                    className="text-decoration-none"
                                    style={{ cursor: 'pointer' }}
                                >
                                    <p className="mb-0 text-dark fw-medium" style={{ lineHeight: '1.5', fontSize: '0.95rem' }}>
                                        {item.principe}
                                    </p>
                                </Link>
                            </div>
                        )}

                        {/* Footer: Juridiction, Chambre, Cour */}
                        <div className="result-meta border-top pt-2 mt-auto">
                            {item.juridiction && (
                                <span className="meta-item">
                                    <i className="bi bi-building meta-icon text-primary"></i>
                                    <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.juridiction) }} />
                                </span>
                            )}
                            {item.chambre && (
                                <span className="meta-item">
                                    <i className="bi bi-door-open meta-icon text-secondary"></i>
                                    <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.chambre) }} />
                                </span>
                            )}
                            {item.cour && (
                                <span className="meta-item">
                                    <i className="bi bi-geo-alt-fill meta-icon text-danger"></i>
                                    <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.cour) }} />
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Removed check for empty query to allow displaying results without a keyword

    return (
        <div className="search-results-page">
            {/* Header / SearchBar - REMOVED (Moved to Sidebar) */}
            <div className="mb-4"></div>

            <div className="container-fluid px-0">
                {loading && !results && (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">{t('search.loading')}</span>
                        </div>
                        <p className="mt-2 text-muted">{t('search.analyzing')}</p>
                    </div>
                )}

                {error && (
                    <div className="alert alert-danger">{error}</div>
                )}

                {results && (
                    <div className="position-relative">
                        {loading && (
                            <div className="loading-overlay">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">{t('search.loading')}</span>
                                </div>
                            </div>
                        )}
                        <div className="results-content">
                            {/* Global View Mode & Sort Switcher */}
                            {(results.jurisprudences?.length > 0 || Object.keys(results.code_articles || {}).length > 0 || results.joarticles?.length > 0) && (
                                <div className="d-flex justify-content-end align-items-center gap-3 mb-4">
                                    {/* Mobile Filter Toggle */}
                                    <button
                                        className="btn btn-outline-secondary btn-sm d-lg-none d-flex align-items-center gap-2"
                                        onClick={() => setShowMobileFilters(!showMobileFilters)}
                                    >
                                        <i className={`bi bi-${showMobileFilters ? 'eye-slash' : 'eye'}`}></i>
                                        <span>{t('search.filters', 'Filtres')}</span>
                                    </button>

                                    {/* Sort Controls - hidden for joarticles (always relevance + date) */}
                                    {docType !== 'joarticles' && (
                                        <div className="d-flex align-items-center gap-2">
                                            <div className="btn-group" role="group" dir="ltr">
                                                <button
                                                    type="button"
                                                    className={`btn btn-outline-secondary btn-sm ${sortBy === 'relevance' ? 'active' : ''}`}
                                                    onClick={() => handleSortChange('relevance', sortOrder)}
                                                    title={t('search.sortByRelevance', 'Pertinence')}
                                                >
                                                    <i className="bi bi-sort-down-alt"></i>
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`btn btn-outline-secondary btn-sm ${sortBy === 'date' ? 'active' : ''} ${docType === 'code_article' ? 'disabled' : ''}`}
                                                    onClick={() => docType !== 'code_article' && handleSortChange('date', sortOrder)}
                                                    title={docType === 'code_article' ? t('search.noDateField', 'Tri par date non disponible pour les articles de code') : t('search.sortByDate', 'Date')}
                                                    disabled={docType === 'code_article'}
                                                >
                                                    <i className="bi bi-calendar"></i>
                                                </button>
                                            </div>
                                            <button
                                                type="button"
                                                className={`btn btn-outline-secondary btn-sm ${sortBy === 'relevance' ? 'disabled' : ''}`}
                                                onClick={() => sortBy !== 'relevance' && handleSortChange(sortBy, sortOrder === 'desc' ? 'asc' : 'desc')}
                                                title={sortBy === 'relevance' ? t('search.noAscRelevance', 'Le tri croissant par pertinence n\'est pas disponible') : (sortOrder === 'desc' ? t('search.sortDesc', 'Décroissant') : t('search.sortAsc', 'Croissant'))}
                                                disabled={sortBy === 'relevance'}
                                            >
                                                <i className={`bi ${sortOrder === 'desc' ? 'bi-sort-down' : 'bi-sort-up'}`}></i>
                                            </button>
                                        </div>
                                    )}

                                    {/* View Mode Controls */}
                                    <div className="btn-group d-none d-md-inline-flex" role="group" dir="ltr">
                                        <button
                                            type="button"
                                            className={`btn btn-outline-secondary btn-sm ${viewMode === 'full' ? 'active' : ''}`}
                                            onClick={() => handleViewModeChange('full')}
                                            title="Liste"
                                        >
                                            <i className="bi bi-list-ul"></i>
                                        </button>
                                        <button
                                            type="button"
                                            className={`btn btn-outline-secondary btn-sm ${viewMode === 'card' ? 'active' : ''}`}
                                            onClick={() => handleViewModeChange('card')}
                                            title="Grille"
                                        >
                                            <i className="bi bi-grid"></i>
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Jurisprudence Results */}
                            {results.jurisprudences && results.jurisprudences.length > 0 && (
                                <section className="mb-5">
                                    <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                                        <h2 className="section-title mb-0" style={{ color: '#981934' }}>
                                            <i className="bi bi-gavel me-2 ms-2"></i>{t('search.jurisprudence')}
                                        </h2>
                                    </div>
                                    <Pagination pagination={pagination} onPageChange={handlePageChange} />
                                    <div className={viewMode === 'card' ? "row" : "list-group"}>
                                        {results.jurisprudences.map((item, index) => (
                                            <JurisprudenceItem key={index} item={item} viewMode={viewMode} />
                                        ))}
                                    </div>
                                    <Pagination pagination={pagination} onPageChange={handlePageChange} />
                                </section>
                            )}

                            {/* Code Articles Results */}
                            {results.code_articles && Object.keys(results.code_articles).length > 0 && (
                                <section className="mb-5">
                                    <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                                        <h2 className="section-title mb-0" style={{ color: '#981934' }}>
                                            <i className="bi bi-book me-2 ms-2"></i>{t('search.codes')}
                                        </h2>
                                    </div>
                                    <Pagination pagination={pagination} onPageChange={handlePageChange} />
                                    <div className={viewMode === 'card' ? "row" : "list-group"}>
                                        {(() => {
                                            const allArticles: any[] = [];
                                            Object.values(results.code_articles).forEach(types => {
                                                Object.values(types).forEach(articles => {
                                                    articles.forEach(article => {
                                                        allArticles.push(article);
                                                    });
                                                });
                                            });

                                            return allArticles.map((article, idx) => {
                                                const containerClass = viewMode === 'card' ? 'col-md-6 col-lg-4 col-xl-3 mb-4' : 'col-12 mb-3';

                                                return (
                                                    <div key={idx} className={containerClass}>
                                                        <div className="result-card type-code animate-fade-in">
                                                            <div className="result-card-body">
                                                                <div className="d-flex w-100 justify-content-between align-items-center mb-3">
                                                                    <div className="d-flex align-items-center gap-2">

                                                                        {article.code_name && (
                                                                            <span className="premium-badge badge-code">
                                                                                <i className="bi bi-book meta-icon text-success"></i> <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(article.code_name) }} />
                                                                            </span>
                                                                        )}
                                                                        {article.from && (
                                                                            <span className={`premium-badge ${article.from === 'corpus' ? 'badge-code' : 'badge-secondary'}`}>
                                                                                {article.from === 'corpus' ? t('search.corpus') : t('search.annexe')}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <div className="d-flex align-items-center gap-2">
                                                                        {/* Score hidden for code_article as requested */}
                                                                        {/* Favori désactivé pour les code_articles : l'id V2 est un id ES
                                                                            composite (chaîne), incompatible avec la colonne user_favorite.document_id (INT).
                                                                            À réactiver après migration de la colonne en VARCHAR. */}
                                                                    </div>
                                                                </div>

                                                                <h5 className="result-title mb-2">
                                                                    <Link
                                                                        to={`/${i18n.language}/detail/code-article/${article.bertilaw_id}`}
                                                                        state={{ searchParams: location.search }}
                                                                        className="text-decoration-none text-dark hover-link"
                                                                    >
                                                                        {t('search.article')} <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(article.numero || '') }} />
                                                                    </Link>
                                                                </h5>

                                                                <div className="result-meta">
                                                                    {article.law_title && (
                                                                        <span className="meta-item">
                                                                            <i className="bi bi-file-text meta-icon"></i> <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(article.law_title) }} />
                                                                        </span>
                                                                    )}
                                                                </div>

                                                                <div className="result-preview">
                                                                    <div className="text-muted small" dangerouslySetInnerHTML={{ __html: sanitizeHtml(article.text || "") }} />
                                                                </div>

                                                                {article.modif_law_title && (
                                                                    <div className="mt-2 pt-2 border-top">
                                                                        <small className="text-info">
                                                                            <i className="bi bi-pencil-square me-1"></i>
                                                                            <strong>Modifié par:</strong> {article.modif_law_title}
                                                                        </small>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            });
                                        })()}
                                    </div>
                                    <Pagination pagination={pagination} onPageChange={handlePageChange} />
                                </section>
                            )}

                            {/* Livres Results */}
                            {results.livres && results.livres.length > 0 && (
                                <section className="mb-5">
                                    <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                                        <h2 className="section-title mb-0" style={{ color: '#981934' }}>
                                            <i className="bi bi-book me-2"></i>
                                            {t('search.types.livres')} ({results.livres.length})
                                        </h2>
                                    </div>
                                    <div className={viewMode === 'card' ? "row" : "list-group"}>
                                        {results.livres.map((livre: any, idx: number) => {
                                            const containerClass = viewMode === 'card' ? 'col-md-6 col-lg-4 col-xl-3 mb-4' : 'col-12 mb-3';
                                            return (
                                                <div key={idx} className={containerClass}>
                                                    <div className="result-card type-livre animate-fade-in">
                                                        <div className="result-card-body">
                                                            <div className="d-flex w-100 justify-content-between align-items-center mb-3">
                                                                <span className="premium-badge badge-livre">
                                                                    <i className="bi bi-book me-1"></i>
                                                                    {t('search.types.livres')}
                                                                </span>
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <button
                                                                        className="btn p-0 fav-btn"
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            e.stopPropagation();
                                                                            toggleFavorite('livre', livre.bertilaw_id, livre.title || 'Livre');
                                                                        }}
                                                                        title={isFavorite('livre', livre.bertilaw_id) ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                                                                    >
                                                                        <i className={`bi ${isFavorite('livre', livre.bertilaw_id) ? 'bi-star-fill' : 'bi-star'}`}></i>
                                                                    </button>
                                                                </div>
                                                            </div>

                                                            <h5 className="result-title mb-2">
                                                                <Link
                                                                    to={`/${i18n.language}/detail/livres/${livre.bertilaw_id}`}
                                                                    state={{ searchParams: location.search }}
                                                                    className="text-decoration-none text-dark hover-link"
                                                                >
                                                                    <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(livre.title || '') }} />
                                                                </Link>
                                                            </h5>

                                                            <div className="result-meta">
                                                                {livre.auteur && (
                                                                    <span className="meta-item">
                                                                        <i className="bi bi-person meta-icon text-primary"></i> <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(livre.auteur) }} />
                                                                    </span>
                                                                )}
                                                                {livre.editeur && (
                                                                    <span className="meta-item">
                                                                        <i className="bi bi-building meta-icon"></i> <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(livre.editeur) }} />
                                                                    </span>
                                                                )}
                                                            </div>

                                                            {livre.text && (
                                                                <div className="result-preview">
                                                                    <div className="text-muted small" dangerouslySetInnerHTML={{ __html: sanitizeHtml((livre.text || "").substring(0, 200) + '...') }} />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </section>
                            )}

                            {/* Theses Results */}
                            {results.theses && results.theses.length > 0 && (
                                <section className="mb-5">
                                    <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                                        <h2 className="section-title mb-0" style={{ color: '#981934' }}>
                                            <i className="bi bi-mortarboard me-2"></i>
                                            {t('search.types.theses')} ({results.theses.length})
                                        </h2>
                                    </div>
                                    <div className={viewMode === 'card' ? "row" : "list-group"}>
                                        {results.theses.map((these: any, idx: number) => {
                                            const containerClass = viewMode === 'card' ? 'col-md-6 col-lg-4 col-xl-3 mb-4' : 'col-12 mb-3';
                                            return (
                                                <div key={idx} className={containerClass}>
                                                    <div className="result-card type-these animate-fade-in">
                                                        <div className="result-card-body">
                                                            <div className="d-flex w-100 justify-content-between align-items-center mb-3">
                                                                <span className="premium-badge badge-these">
                                                                    <i className="bi bi-mortarboard me-1"></i>
                                                                    {t('search.types.theses')}
                                                                </span>
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <button
                                                                        className="btn p-0 fav-btn"
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            e.stopPropagation();
                                                                            toggleFavorite('these', these.bertilaw_id, these.title || 'Thèse');
                                                                        }}
                                                                        title={isFavorite('these', these.bertilaw_id) ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                                                                    >
                                                                        <i className={`bi ${isFavorite('these', these.bertilaw_id) ? 'bi-star-fill' : 'bi-star'}`}></i>
                                                                    </button>
                                                                </div>
                                                            </div>

                                                            <h5 className="result-title mb-2">
                                                                <Link
                                                                    to={`/${i18n.language}/detail/theses/${these.bertilaw_id}`}
                                                                    state={{ searchParams: location.search }}
                                                                    className="text-decoration-none text-dark hover-link"
                                                                >
                                                                    <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(these.title || '') }} />
                                                                </Link>
                                                            </h5>

                                                            <div className="result-meta">
                                                                {these.auteur && (
                                                                    <span className="meta-item">
                                                                        <i className="bi bi-person meta-icon text-info"></i> <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(these.auteur) }} />
                                                                    </span>
                                                                )}
                                                                {these.universite && (
                                                                    <span className="meta-item">
                                                                        <i className="bi bi-building meta-icon"></i> <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(these.universite) }} />
                                                                    </span>
                                                                )}
                                                            </div>

                                                            {these.text && (
                                                                <div className="result-preview">
                                                                    <div className="text-muted small" dangerouslySetInnerHTML={{ __html: sanitizeHtml((these.text || "").substring(0, 200) + '...') }} />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </section>
                            )}

                            {/* JOArticles Results */}
                            {results.joarticles && results.joarticles.length > 0 && (
                                <section className="mb-5">
                                    <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                                        <h2 className="section-title mb-0" style={{ color: '#981934' }}>
                                            <i className="bi bi-newspaper me-2"></i>
                                            {t('search.types.joarticles')} ({results.joarticles.length})
                                        </h2>
                                    </div>
                                    <Pagination pagination={pagination} onPageChange={handlePageChange} />

                                    <div className={viewMode === 'card' ? "row" : "list-group"}>
                                        {results.joarticles.map((article: any, idx: number) => {
                                            const containerClass = viewMode === 'card' ? 'col-md-6 col-lg-4 col-xl-3 mb-4' : 'col-12 mb-3';
                                            const rawTitle = i18n.language === 'ar'
                                                ? (article.titlear || article.titlefr || article.title)
                                                : (article.titlefr || article.title || article.titlear);
                                            const displayTitle = rawTitle;
                                            const displayText = article.text;
                                            const natureName = i18n.language === 'ar'
                                                ? (article.nature_name_ar || article.nature_name)
                                                : (article.nature_name || article.nature_name_fr);

                                            return (
                                                <div key={idx} className={containerClass}>
                                                    <div className="result-card type-jo animate-fade-in">
                                                        <div className="result-card-body">
                                                            <div className="d-flex w-100 justify-content-between align-items-center mb-3">
                                                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                                                    <span className="premium-badge badge-jo">
                                                                        <i className="bi bi-newspaper me-1"></i>
                                                                        {t('search.types.joarticles')}
                                                                    </span>
                                                                    {natureName && (
                                                                        <span className="badge bg-secondary" style={{ fontSize: '0.7rem' }}>
                                                                            {natureName}
                                                                        </span>
                                                                    )}
                                                                    {article.date_publication && (
                                                                        <small className="text-muted fw-medium">
                                                                            <i className="bi bi-calendar3 me-1"></i>
                                                                            {article.date_publication}
                                                                        </small>
                                                                    )}
                                                                    {article.datesignature && (
                                                                        <small className="text-muted fw-medium">
                                                                            <i className="bi bi-pen me-1"></i>
                                                                            {article.datesignature}
                                                                        </small>
                                                                    )}
                                                                    {article.score !== undefined && (
                                                                        <small className="text-muted" style={{ fontSize: '0.65rem', opacity: 0.6 }}>
                                                                            <i className="bi bi-speedometer2 me-1"></i>
                                                                            {article.score}
                                                                        </small>
                                                                    )}
                                                                </div>
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <button
                                                                        className="btn p-0 fav-btn"
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            e.stopPropagation();
                                                                            toggleFavorite('joarticle', article.bertilaw_id, displayTitle || 'Article JO');
                                                                        }}
                                                                        title={isFavorite('joarticle', article.bertilaw_id) ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                                                                    >
                                                                        <i className={`bi ${isFavorite('joarticle', article.bertilaw_id) ? 'bi-star-fill' : 'bi-star'}`}></i>
                                                                    </button>
                                                                </div>
                                                            </div>

                                                            {/* Language availability warning */}
                                                            {!article.has_content && article.has_other_lang && (
                                                                <div className="alert alert-warning py-2 px-3 mb-2 d-flex align-items-center justify-content-between" style={{ fontSize: '0.85rem' }}>
                                                                    <span>
                                                                        <i className="bi bi-exclamation-triangle me-2"></i>
                                                                        {i18n.language === 'ar' ? 'غير متوفر بالعربية' : 'Non disponible en français'}
                                                                    </span>
                                                                    <Link
                                                                        to={`/${i18n.language === 'ar' ? 'fr' : 'ar'}/detail/jo/${article.bertilaw_id}`}
                                                                        className="btn btn-sm btn-outline-warning"
                                                                    >
                                                                        {i18n.language === 'ar' ? 'Voir en français' : 'عرض بالعربية'}
                                                                    </Link>
                                                                </div>
                                                            )}

                                                            <h5 className="result-title mb-3">
                                                                <Link
                                                                    to={`/${i18n.language}/detail/jo/${article.bertilaw_id}`}
                                                                    state={{ searchParams: location.search }}
                                                                    className="text-decoration-none text-dark hover-link"
                                                                >
                                                                    <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(displayTitle || '') }} />
                                                                </Link>
                                                            </h5>

                                                            <div className="result-meta">
                                                                {article.jo_numero && (
                                                                    <span className="meta-item">
                                                                        <i className="bi bi-newspaper meta-icon"></i> N° journal: <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(article.jo_numero) }} />
                                                                    </span>
                                                                )}
                                                                {article.numero && (
                                                                    <span className="meta-item">
                                                                        <i className="bi bi-hash meta-icon"></i> N° Texte: <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(article.numero) }} />
                                                                    </span>
                                                                )}
                                                            </div>

                                                            {displayText && (
                                                                <div className="result-preview">
                                                                    <div className="text-muted small" dangerouslySetInnerHTML={{ __html: sanitizeHtml(displayText) }} />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <Pagination pagination={pagination} onPageChange={handlePageChange} />
                                </section>
                            )}

                            {/* Fallback if no results */}
                            {(!results.jurisprudences?.length && !Object.keys(results.code_articles || {}).length &&
                                !results.livres?.length && !results.theses?.length && !results.joarticles?.length) && (
                                    <div className="text-center py-5">
                                        <p className="text-muted fs-5">{t('search.noResults')}</p>
                                    </div>
                                )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SearchResults;

